import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal, flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { fontFamilyName, type FontStem } from "./fonts";
import { collectFonts, type PageSpec, type TextNode } from "./ir";
import { textLayoutByNode, type TextLayout } from "./lineBreaks";
import { flowWidths, measureFlow, paginate, type FlowMeasurement, type PagedDoc } from "./paginate";
import { FlowView } from "./renderHtml";

// Browser-side layout: loads the fonts a design uses, renders its flows in a
// hidden, unscaled host at real size, measures them and paginates. The live
// preview (usePagedLayout) and the PDF export (layoutDocument) run the exact
// same steps on the same page design, so they always agree on every page.

/** Inherited text settings reset so the site's CSS can never change layout. */
export const RESUME_ROOT_STYLE: CSSProperties = {
  textTransform: "none",
  wordSpacing: "normal",
  letterSpacing: "normal",
  textIndent: 0,
  fontVariant: "normal",
  fontFeatureSettings: "normal",
  fontVariationSettings: "normal",
  fontKerning: "normal",
  fontSynthesis: "none",
  hyphens: "manual",
  direction: "ltr",
  textAlign: "left",
  lineHeight: "normal",
};

const HOST_STYLE: CSSProperties = {
  ...RESUME_ROOT_STYLE,
  position: "fixed",
  left: -100000,
  top: 0,
  visibility: "hidden",
  pointerEvents: "none",
  zIndex: -1,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  contain: "layout style",
};

/** Every font file a page design draws with (body text plus the footer). */
export function specFonts(spec: PageSpec): Set<FontStem> {
  const into = new Set<FontStem>([spec.footer.font]);
  collectFonts(spec.body, into);
  if (spec.columns) {
    collectFonts(spec.columns.left, into);
    collectFonts(spec.columns.right, into);
  }
  return into;
}

const loaded = new Set<FontStem>();
const loading = new Map<FontStem, Promise<void>>();

export function fontsLoaded(stems: Iterable<FontStem>): boolean {
  for (const s of stems) if (!loaded.has(s)) return false;
  return true;
}

/** Loads (once) the @font-face of every stem; resolves when all are usable. */
export function loadFonts(stems: Iterable<FontStem>): Promise<void> {
  const waits: Promise<void>[] = [];
  for (const stem of stems) {
    if (loaded.has(stem)) continue;
    let p = loading.get(stem);
    if (!p) {
      p = document.fonts
        .load(`12px "${fontFamilyName(stem)}"`)
        .then(
          () => void loaded.add(stem),
          () => void loaded.add(stem)
        );
      loading.set(stem, p);
    }
    waits.push(p);
  }
  return Promise.all(waits).then(() => undefined);
}

type FlowRefs = {
  body: RefObject<HTMLDivElement | null>;
  left: RefObject<HTMLDivElement | null>;
  right: RefObject<HTMLDivElement | null>;
};

/** The design's flows at full size — what gets measured. */
function MeasureFlows({ spec, refs }: { spec: PageSpec; refs: FlowRefs }) {
  const w = flowWidths(spec);
  if (spec.columns) {
    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
        <FlowView nodes={spec.columns.left} width={w.left} innerRef={refs.left} />
        <FlowView nodes={spec.columns.right} width={w.right} innerRef={refs.right} />
      </div>
    );
  }
  return <FlowView nodes={spec.body} width={w.body} innerRef={refs.body} />;
}

function measure(spec: PageSpec, refs: FlowRefs): { body?: FlowMeasurement; left?: FlowMeasurement; right?: FlowMeasurement } | null {
  if (spec.columns) {
    const l = refs.left.current;
    const r = refs.right.current;
    if (!l || !r) return null;
    return { left: measureFlow(l, spec.columns.left), right: measureFlow(r, spec.columns.right) };
  }
  const b = refs.body.current;
  return b ? { body: measureFlow(b, spec.body) } : null;
}

/**
 * Paginates [spec] for an on-screen preview. Returns the paged document
 * (null until the fonts are in and the first measurement is done; it then
 * keeps the previous result while a new design is measured) and the hidden
 * measuring host, which the caller must render. The host only exists while
 * a measurement is pending, so idle previews carry no hidden copy.
 */
export function usePagedLayout(spec: PageSpec | null): { doc: PagedDoc | null; host: React.ReactNode; pending: boolean } {
  const refs: FlowRefs = { body: useRef(null), left: useRef(null), right: useRef(null) };
  const [doc, setDoc] = useState<PagedDoc | null>(null);
  const [fontTick, setFontTick] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Portals need document.body; render the host only after hydration.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time client-only switch
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!spec || !mounted) return;
    const stems = specFonts(spec);
    if (!fontsLoaded(stems)) {
      let live = true;
      loadFonts(stems).then(() => live && setFontTick((t) => t + 1));
      return () => {
        live = false;
      };
    }
    const m = measure(spec, refs);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- layout must be read from the DOM after render
    if (m) setDoc(paginate(spec, m));
    // refs are stable objects; re-measure when the design or the fonts change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, fontTick, mounted]);

  const pending = !!spec && doc?.spec !== spec;
  const host =
    spec && mounted && pending
      ? createPortal(
          <div aria-hidden="true" style={HOST_STYLE}>
            <MeasureFlows spec={spec} refs={refs} />
          </div>,
          document.body
        )
      : null;
  return { doc, host, pending };
}

/**
 * Lays [spec] out off-screen with its fonts loaded and returns the paged
 * document plus every text block's line breaks and width — everything the
 * PDF needs.
 */
export async function layoutDocument(spec: PageSpec): Promise<{ doc: PagedDoc; texts: Map<TextNode, TextLayout> }> {
  await loadFonts(specFonts(spec));
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  Object.assign(host.style, {
    position: "fixed",
    left: "-100000px",
    top: "0",
    visibility: "hidden",
    pointerEvents: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  });
  for (const [k, v] of Object.entries(RESUME_ROOT_STYLE)) {
    (host.style as unknown as Record<string, string>)[k] = typeof v === "number" ? String(v) : (v as string);
  }
  document.body.appendChild(host);
  const root = createRoot(host);
  const refs: FlowRefs = { body: { current: null }, left: { current: null }, right: { current: null } };
  try {
    flushSync(() => root.render(<MeasureFlows spec={spec} refs={refs} />));
    await document.fonts.ready;
    const m = measure(spec, refs);
    if (!m) throw new Error("Resume layout could not be measured");
    const doc = paginate(spec, m);
    const texts = new Map<TextNode, TextLayout>();
    if (spec.columns) {
      textLayoutByNode(refs.left.current!, spec.columns.left, texts);
      textLayoutByNode(refs.right.current!, spec.columns.right, texts);
    } else {
      textLayoutByNode(refs.body.current!, spec.body, texts);
    }
    return { doc, texts };
  } finally {
    root.unmount();
    host.remove();
  }
}
